const THUMB_WIDTH = 640;
const THUMB_JPEG_QUALITY = 0.82;
const SEEK_TIMEOUT_MS = 8000;
/** Extra tries when the captured picture is still black (frame not decoded yet). */
const BLACK_RETRIES = 6;
const RETRY_DELAY_MS = 150;

type FrameCallbackVideo = HTMLVideoElement & {
  requestVideoFrameCallback?: (callback: () => void) => number;
};

/** Resolves once the seeked frame is decoded (frame callback where supported), or after `maxMs`. */
const frameReady = (video: FrameCallbackVideo, maxMs: number) =>
  new Promise<void>((resolve) => {
    const timer = window.setTimeout(resolve, maxMs);
    const done = () => { window.clearTimeout(timer); resolve(); };
    if (video.requestVideoFrameCallback) video.requestVideoFrameCallback(done);
    else if (video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) done();
    else video.addEventListener('loadeddata', done, { once: true });
  });

/** True when the drawn picture is (almost) black — sampled on a small grid. */
const isBlack = (ctx: CanvasRenderingContext2D, width: number, height: number) => {
  const { data } = ctx.getImageData(0, 0, width, height);
  const step = Math.max(4, Math.floor(data.length / 4 / 400)) * 4;
  let sum = 0;
  let n = 0;
  for (let i = 0; i < data.length; i += step) {
    sum += data[i] + data[i + 1] + data[i + 2];
    n++;
  }
  return n > 0 && sum / (n * 3) < 6;
};

/**
 * Captures a JPEG thumbnail from a video Blob at `seekTime` seconds.
 * Returns null silently on any failure — callers should treat null as "no thumbnail".
 *
 * The `seeked` event can fire before the frame is decoded (notably with hardware decoding), which
 * draws a black picture: the capture waits for the decoded frame and retries while it is black.
 */
export async function generateThumbnail(
  videoBlob: Blob,
  seekTime = 1
): Promise<Blob | null> {
  if (typeof document === 'undefined') return null;

  return new Promise((resolve) => {
    const video = document.createElement('video') as FrameCallbackVideo;
    video.muted = true;
    video.playsInline = true;
    video.preload = 'auto';

    const objectUrl = URL.createObjectURL(videoBlob);
    let settled = false;

    const cleanup = () => {
      URL.revokeObjectURL(objectUrl);
      video.src = '';
      video.remove();
    };

    const settle = (result: Blob | null) => {
      if (settled) return;
      settled = true;
      clearTimeout(timeout);
      cleanup();
      resolve(result);
    };

    const timeout = setTimeout(() => settle(null), SEEK_TIMEOUT_MS);

    video.onloadedmetadata = () => {
      const target = Math.min(seekTime, video.duration > 0 ? video.duration * 0.1 : 0);
      video.currentTime = target > 0 ? target : 0;
    };

    video.onseeked = async () => {
      video.onseeked = null;
      try {
        const aspectRatio = video.videoHeight > 0 ? video.videoHeight / video.videoWidth : 9 / 16;
        const canvas = document.createElement('canvas');
        canvas.width = THUMB_WIDTH;
        canvas.height = Math.round(THUMB_WIDTH * aspectRatio);
        const ctx = canvas.getContext('2d', { willReadFrequently: true });
        if (!ctx) { settle(null); return; }

        await frameReady(video, 1000);
        for (let attempt = 0; ; attempt++) {
          ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
          // A genuinely dark first second is kept after the last try.
          if (!isBlack(ctx, canvas.width, canvas.height) || attempt >= BLACK_RETRIES || settled) break;
          await new Promise((r) => setTimeout(r, RETRY_DELAY_MS));
        }
        canvas.toBlob(
          (blob) => settle(blob),
          'image/jpeg',
          THUMB_JPEG_QUALITY
        );
      } catch {
        settle(null);
      }
    };

    video.onerror = () => settle(null);
    video.src = objectUrl;
  });
}
